<template>
	<header>
		<div class="header-content left" role="banner">
			<ja-icon v-if="title" aria-hidden="true">
				<slot name="icon"></slot>
			</ja-icon>
			<div class="title-wrapper">
				<div class="title">
					{{ title }}
				</div>
				<div class="heading-5">
					{{ subtitle }}
				</div>
			</div>
		</div>
		<div class="header-content center">
			<slot name="centered-content"></slot>
		</div>
		<div class="header-content-wrapper">
			<div class="header-content right">
				<div class="ja-logo-wrapper" role="banner">
					<img
						src="@/assets/images/latest_ja_fp_logo.svg"
						alt="Junior Achievement Logo"
						class="ja-logo"
					/>
				</div>
				<div v-if="userData" class="header-right-buttons">
					<ja-button
						v-if="showHome"
						class="small transparent"
						name="home-page"
						:disabled="disabled"
						:aria-label="$t('goHomePage')"
						@click.native.once="goToHome"
					>
						<svgicon name="home"></svgicon>
					</ja-button>
					<ja-button
						v-if="showIntro"
						class="small transparent"
						name="intro"
						:disabled="disabled"
						:aria-label="$t('openOnboarding')"
						@click.native="openOnboardingTutorial"
					>
						<svgicon name="intro"></svgicon>
					</ja-button>
					<ja-button
						v-if="!disableLogout"
						ref="header-menu"
						:disabled="disabled"
						:class="[
							'small',
							'transparent',
							'profile-menu',
							{ 'skip-disable': isTutorial && !isLogoutModalOpened }
						]"
						:aria-label="$t('toggleDropdownMenu')"
						:aria-expanded="openMenu.toString()"
						name="header-menu"
						@click.native="toggleMenu"
					>
						<svgicon name="profile"></svgicon>
						<svgicon class="arrow-down" name="arrow-right"></svgicon>
					</ja-button>
				</div>
				<ja-tooltip
					v-if="userData && !disableLogout"
					v-show="openMenu"
					v-click-outside="{
						handler: 'closeMenu',
						exclude: ['header-menu']
					}"
					class="top btn-group__content btn-group__content--right btn-group btn-group--vertical"
				>
					<div class="btn-group__item body-text-1 capitalize boldize">
						{{ userData.firstName }} {{ userData.lastName }}
					</div>
					<div
						v-if="userData"
						class="header-right-buttons"
						style="text-align: right;"
					>
						<ja-button
							v-if="showHome"
							class="small transparent"
							name="home-page"
							:disabled="disabled"
							:aria-label="$t('goHomePage')"
							@click.native.once="goToHome"
						>
							<svgicon name="home"></svgicon>
						</ja-button>
						<ja-button
							v-if="showIntro"
							class="small transparent"
							name="intro"
							:disabled="disabled"
							:aria-label="$t('openOnboarding')"
							@click.native="openOnboardingTutorial"
						>
							<svgicon name="intro"></svgicon>
						</ja-button>
					</div>
					<div class="btn-group__item body-text-1 lowercase">
						{{ userData.email }}
					</div>
					<div
						v-if="userData.studentId"
						class="btn-group__item body-text-1 boldize"
					>
						{{ $t('studentId') }}
						{{ userData.studentId }}
					</div>
					<div
						v-if="userData.lastSimulationName"
						class="btn-group__item body-text-1"
					>
						{{ $t('welcomeBackTextHeader') }}
						{{ userData.lastSimulationName }}
					</div>
					<div>
						<button
							v-if="profileData"
							class="btn btn-link text--bold-600"
							@click="goToProfile"
						>
							{{ $t('viewProfile') }}
						</button>
					</div>
					<ja-future-tracker2
						v-if="profileData"
						class="mt--3"
						style="float: right;"
						:children="profileData.studentChildren"
						:num-pets="profileData.numberOfPets"
						:married="isMarried"
					></ja-future-tracker2>
					<ja-button
						v-if="isChooseSimVisible"
						name="chooseSim"
						class="transparent body-text-1"
						:aria-label="$t('chooseSimulation')"
						:disabled="disabled"
						@click.native="chooseSimulation"
					>
						{{ $t('changeSimulation') }}
					</ja-button>
					<ja-button
						v-if="isSimReportVisible"
						name="chooseSimReport"
						class="transparent body-text-1"
						:aria-label="$t('reports')"
						:disabled="disabled"
						@click.native="chooseSimReport"
					>
						{{ $t('reports') }}
					</ja-button>
					<ja-button
						v-if="!isViewAsStudentEnabled"
						name="logout"
						class="transparent body-text-1"
						:aria-label="$t('logОut')"
						:disabled="disabled"
						@click.native="openLogoutDialog"
					>
						{{ $t('logОut') }}
					</ja-button>
					<ja-button
						v-else
						name="closeSimulation"
						class="transparent body-text-1"
						:aria-label="$t('closeSimulation')"
						:disabled="disabled"
						@click.native="closeSimulation"
					>
						{{ $t('closeSimulation') }}
					</ja-button>
				</ja-tooltip>
			</div>

			<div>
				<slot name="admin-facility"></slot>
			</div>
		</div>
	</header>
</template>
<script>
import '@/components/icons/home';
import '@/components/icons/intro';
import '@/components/icons/profile';
import '@/components/icons/arrow-right';
import FutureTracker2 from '@/components/HeadsupDisplay/FutureTracker2';
import { UserData, ProfileData } from '@/mixins';
import { Icon, Tooltip, Button } from '@ui';
import {
	getChooseSimulation,
	swapChooseSimulation,
	setStorageRedirectTo
} from '@/helpers/storageFunctions';
import { maritalStatus } from '@/helpers/constants';

import graphqlClient from '@/helpers/graphqlClient';

export default {
	components: {
		jaIcon: Icon,
		jaTooltip: Tooltip,
		jaButton: Button,
		jaFutureTracker2: FutureTracker2
	},
	mixins: [UserData, ProfileData],
	props: {
		title: {
			type: String,
			default: ''
		},
		subtitle: {
			type: String,
			default: ''
		},
		disableLogout: {
			type: Boolean,
			default: false
		},
		showHome: {
			type: Boolean,
			default: false
		},
		showIntro: {
			type: Boolean,
			default: false
		},
		isTutorial: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			openMenu: false,
			activeConnections: 1
		};
	},
	computed: {
		isViewAsStudentEnabled() {
			return this.userData.isViewAsStudentEnabled;
		},
		isSimReportVisible() {
			return (
				false &&
				this.userData.showReportsInNavigationBar &&
				this.$route.name !== 'simReport'
			);
		},
		isChooseSimVisible() {
			return (
				this.userData.manyActiveSimulations &&
				this.$route.name !== 'chooseSimulation'
			);
		},
		isMarried() {
			return this.profileData.cmsMaritalStatus.status.toLowerCase() ===
				this.$t('married') ||
				this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					maritalStatus.MARRIED.toLowerCase()
				? true
				: false;
		}
	},
	methods: {
		sinceDate() {
			const now = Date.now();
			const simDate = new Date(this.userData.lastSimulationPlayedDate);
			const sinceInMilliseconds = now - simDate;

			let result = '';
			if (!isNaN(sinceInMilliseconds)) {
				const sinceInSeconds = Math.floor(sinceInMilliseconds / 1000);
				const sinceInMinutes = Math.floor(sinceInSeconds / 60);
				const sinceInHours = Math.floor(sinceInMinutes / 60);
				const sinceInDays = Math.floor(sinceInHours / 60);

				if (sinceInDays > 0) {
					result = `${sinceInDays} ${this.$tc(
						'welcomeBackSinceDays',
						sinceInDays
					)}`;
				} else if (sinceInHours > 0) {
					result = `${sinceInHours} ${this.$tc(
						'welcomeBackSinceHours',
						sinceInHours
					)}`;
				} else if (sinceInMinutes > 1) {
					result = `${sinceInMinutes} ${this.$tc(
						'welcomeBackSinceMinutes',
						sinceInMinutes
					)}`;
				} else {
					result = this.$t('welcomeBackSinceNow');
				}
			}

			return result;
		},
		isLogoutModalOpened() {
			return this.$store.getters.showLogoutModal;
		},
		closeMenu() {
			this.openMenu = false;
		},
		toggleMenu() {
			this.$store.dispatch('getActiveNumberOfConnections').then(response => {
				this.activeConnections = response.data;
			});
			this.openMenu = !this.openMenu;
		},
		openOnboardingTutorial() {
			this.$store.commit('toggleOnboarding', true);
		},
		openLogoutDialog() {
			this.$store.commit('openLogoutModal');
		},
		goToHome() {
			// refetch categories' info if category is current page
			// and home button is pressed.
			// This will prevent post-errors if you open the same category again.
			if (this.$route.name === 'category') {
				graphqlClient.stop();
				this.$store.commit('resetLifeStageData');
				this.$store.commit('resetCategories');
			}

			this.$store.commit('setLoading', true);
			this.$router.push('/home');
		},
		goToProfile() {
			this.$store.commit('setLoading', true);
			this.$router.push({ name: 'profile' });
		},
		swapSimulationsIfAny() {
			if (getChooseSimulation().id) {
				swapChooseSimulation();
			}
		},
		chooseSimulation() {
			this.$store.commit('setLoading', true);

			this.swapSimulationsIfAny();

			setStorageRedirectTo(this.$route.name);

			this.$router.push('/chooseSimulation');
		},
		chooseSimReport() {
			this.$store.commit('setLoading', true);

			this.swapSimulationsIfAny();

			setStorageRedirectTo(this.$route.name);

			this.$router.push({ name: 'simReport' });
		},
		closeSimulation() {
			window.close();
		}
	}
};
</script>
<style lang="scss" scoped>
header {
	display: flex;
	align-items: center;
	width: 100%;
	height: 100px;
	padding: 0 5%;
	background-color: #ffffff;
	box-shadow: 0 15px 30px 0 rgba(0, 148, 240, 0.14);
	border-bottom: 1px solid $container-border;
	font-family: 'RobotoMedium';
	//text-transform: capitalize;
	z-index: 10;
	flex-shrink: 0;
	outline: 0;

	.header-content-wrapper {
		margin-top: 15px;
	}
	.title-wrapper {
		flex-grow: 1;
		margin-left: 16px;

		.title {
			font-size: 18px;
			line-height: 27px;
			letter-spacing: 0.15px;
			color: $font-header;

			@media (min-width: $breakpoint-mid) {
				font-size: 20px;
				line-height: 30px;
				letter-spacing: 0.15px;
			}
		}
	}

	.header-content {
		display: flex;
		align-items: center;

		&.left {
			justify-content: flex-start;
			flex: 0 0 auto;

			@media (min-width: $breakpoint-mid) {
				flex-basis: 350px;
			}
		}

		&.center {
			flex: 1 0 auto;
			justify-content: center;
		}

		&.right {
			flex: 0 0 auto;
			justify-content: flex-end;
			position: relative;
			padding-left: 16px;

			.ja-logo-wrapper {
				display: flex;
				align-items: center;
			}

			.ja-logo {
				height: 26px;
			}

			.header-right-buttons {
				margin-left: 48px;

				button {
					min-width: auto;
					width: auto;
					height: auto;
					padding: 4px;
				}

				svg {
					height: 20px;
					width: 20px;
					fill: $primary-state;
					@media (any-hover: hover) {
						&:hover {
							fill: $hover-state;
							cursor: pointer;
						}
					}
				}

				.profile-menu {
					display: inline-block;

					@media (any-hover: hover) {
						&:hover svg {
							fill: $hover-state;
							cursor: pointer;
						}
					}

					.arrow-down {
						transform: rotate(90deg);
						width: 8px;
						margin-left: 8px;
					}
				}
			}

			.tooltip-msg {
				width: auto;
				right: -30px;
				top: 32px;

				.content-align {
					&--right {
						text-align: right;
					}
				}
			}
		}
	}

	.btn-group__content {
		button {
			margin: 4px 0;
			font-weight: 600;
		}

		> div {
			width: 100%;
		}
		.btn-group__item {
			font-size: 16px;

			> span {
				&.red {
					color: red;
				}
			}
		}
	}
}
.lowercase {
	text-transform: lowercase;
}
.capitalize {
	text-transform: capitalize;
}
.boldize {
	font-weight: 800;
}
</style>
s
